"use client";

import React, { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  ensureMercadoPagoInitialized,
  getMercadoPagoInitError,
  isMercadoPagoScriptLoaded,
} from "@/lib/mercadopago";
import { TarotApiService } from "@/services/tarotApi";
import { pricingService } from "@/services/pricing";
import MercadoPagoStatusScreen from "./MercadoPagoStatusScreen";
import { useI18n } from "@/i18n/I18nProvider";
import { ErrorLogService, ErrorSeverity } from "@/services/errorLog";

// Keep the dynamic component identity stable across renders. Creating this
// inside MercadoPagoPaymentBrick recreates the wrapper on every state update,
// which can make the Mercado Pago Brick unmount while it is still initializing.
const Payment = dynamic(
  () => import("@mercadopago/sdk-react").then((module) => module.Payment),
  { ssr: false },
);

// Lightweight types to avoid widespread 'any'
type MPFormData = Record<string, unknown> & {
  token?: string;
  payment_method_id?: string;
  paymentMethodId?: string;
  installments?: number;
  payment_method?: { id?: string };
  paymentMethod?: { id?: string };
  payer?: { email?: string };
  email?: string;
};
type MPPaymentMethod = Record<string, unknown> & {
  type?: string;
  payment_type_id?: string;
  paymentTypeId?: string;
  payment_method_id?: string;
  id?: string;
};

// Helper to safely stringify objects for logging (prevents circular references)
function safeStringify(obj: unknown, maxDepth = 3): string {
  const seen = new WeakSet<object>();
  const stringify = (value: unknown, depth: number): unknown => {
    if (depth > maxDepth) return "[Max Depth]";
    if (value === null) return null;
    if (typeof value !== "object") return value;
    if (seen.has(value)) return "[Circular]";

    seen.add(value);

    if (Array.isArray(value)) {
      return value.map((item: unknown) => stringify(item, depth + 1));
    }

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = stringify(val, depth + 1);
    }
    return result;
  };

  try {
    return JSON.stringify(stringify(obj, 0), null, 2);
  } catch (err) {
    return `[Stringify Error: ${
      err instanceof Error ? err.message : String(err)
    }]`;
  }
}

function sanitizeEmail(email?: string): string | undefined {
  if (typeof email !== "string") return undefined;
  const withoutZeroWidth = email.replace(/[\u200B-\u200D\uFEFF]/g, "");
  const trimmed = withoutZeroWidth.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isValidEmail(email?: string): boolean {
  const sanitized = sanitizeEmail(email);
  return Boolean(sanitized && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized));
}

function getEmailDiagnostics(emailRaw?: string): Record<string, unknown> {
  const raw = typeof emailRaw === "string" ? emailRaw : "";
  const sanitized = sanitizeEmail(emailRaw) || "";
  return {
    emailRaw: raw,
    emailSanitized: sanitized,
    emailLength: raw.length,
    emailSanitizedLength: sanitized.length,
    emailHasWhitespace: /\s/.test(raw),
    emailHasZeroWidth: /[\u200B-\u200D\uFEFF]/.test(raw),
    emailHasNonAscii: /[^\x00-\x7F]/.test(raw),
  };
}

/**
 * Valida e registra dados do Payment Brick, lidando com diferentes formatos que o MP pode retornar.
 * O MercadoPago às vezes retorna selectedPaymentMethod como string (ex: "bank_transfer" para Pix)
 * ao invés de objeto. Esta função lida com ambos os casos.
 */
function validateBrickSubmission(
  selectedPaymentMethod: unknown,
  formData: unknown,
  requestId: number,
): {
  spm: MPPaymentMethod;
  fdata: MPFormData;
  extractedEmail: string | undefined;
} {
  // Log raw data BEFORE any transformation
  ErrorLogService.logInfo("MP Brick Raw Data", {
    component: "MercadoPagoPaymentBrick",
    extra_data: {
      requestId,
      raw_selectedPaymentMethod_type: typeof selectedPaymentMethod,
      raw_selectedPaymentMethod: safeStringify(selectedPaymentMethod),
      raw_formData_type: typeof formData,
      raw_formData: safeStringify(formData),
    },
  }).catch(() => {});

  // Handle selectedPaymentMethod - can be string or object
  let spm: MPPaymentMethod;
  if (typeof selectedPaymentMethod === "string") {
    // MercadoPago sometimes returns a string like "bank_transfer" for Pix
    spm = {
      type: selectedPaymentMethod,
      payment_type_id: selectedPaymentMethod,
    };
    ErrorLogService.logInfo(
      "MP selectedPaymentMethod was string, converted to object",
      {
        component: "MercadoPagoPaymentBrick",
        extra_data: {
          requestId,
          originalValue: selectedPaymentMethod,
          convertedTo: safeStringify(spm),
        },
      },
    ).catch(() => {});
  } else if (
    selectedPaymentMethod &&
    typeof selectedPaymentMethod === "object"
  ) {
    spm = selectedPaymentMethod as MPPaymentMethod;
  } else {
    throw new Error(
      `Invalid selectedPaymentMethod: expected object or string, got ${typeof selectedPaymentMethod}`,
    );
  }

  // Ensure formData is an object
  if (!formData || typeof formData !== "object") {
    throw new Error(
      `Invalid formData: expected object, got ${typeof formData}`,
    );
  }

  const fdata = formData as MPFormData;

  // Extract email from the proper location
  let extractedEmail: string | undefined;
  if (fdata.payer && typeof fdata.payer === "object") {
    extractedEmail = (fdata.payer as { email?: string }).email;
  }
  if (!extractedEmail && typeof fdata.email === "string") {
    extractedEmail = fdata.email;
  }

  return { spm, fdata, extractedEmail };
}

type Props = Readonly<{
  requestId: number;
  onSuccess?: (status: "approved" | "authorized") => void;
  amountOverrideCents?: number;
  payerEmail?: string;
}>;

export default function MercadoPagoPaymentBrick({
  requestId,
  onSuccess,
  amountOverrideCents,
  payerEmail,
}: Props) {
  const { t } = useI18n();
  const getEmailErrorMessage = useCallback((message: string, fallbackKey: string) => {
    // The API includes a suggestion in Portuguese or English for domain typos.
    const suggestion = message.match(
      /(?:Talvez voc[eê] quis dizer|Did you mean)\s+'([^'\s]+@[^'\s]+)'/i,
    )?.[1];
    if (suggestion && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suggestion)) {
      return t("step8.error.email_typo").replace("{email}", suggestion);
    }
    return t(fallbackKey);
  }, [t]);
  const [amount, setAmount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showStatus, setShowStatus] = useState<boolean>(false);
  const [pixQrBase64, setPixQrBase64] = useState<string | undefined>(undefined);
  const [pixQrText, setPixQrText] = useState<string | undefined>(undefined);
  const [pixTicketUrl, setPixTicketUrl] = useState<string | undefined>(
    undefined,
  );
  const [lastPaymentId, setLastPaymentId] = useState<string | undefined>(
    undefined,
  );
  const [pixCopyFeedback, setPixCopyFeedback] = useState<{
    id: number;
    message: string;
    tone: "success" | "error" | "info";
  } | null>(null);
  const paymentBrickId = useMemo(
    () => `paymentBrick_container_${requestId}`,
    [requestId],
  );
  const pixCopyResetTimeoutRef = useRef<number | null>(null);
  const pixQrTextRef = useRef<HTMLTextAreaElement | null>(null);

  // NOTE: Don't validate payerEmail prop here - MercadoPago Brick will collect
  // the email during onSubmit. The prop is just a pre-fill hint.
  // Validation happens in onSubmit when we have the actual data from the form.

  const logInitialization = useEffectEvent((finalAmount: number, mpInitialized: boolean) => {
    void ErrorLogService.logInfo("MP Brick Initialized", {
      component: "MercadoPagoPaymentBrick",
      extra_data: {
        requestId,
        amountCents: finalAmount,
        hasPayerEmailProp: Boolean(payerEmail),
        payerEmailValid: payerEmail ? isValidEmail(payerEmail) : null,
        mpInitialized,
        scriptLoaded: isMercadoPagoScriptLoaded(),
      },
    }).catch(() => {});
  });

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        // Try to initialize MercadoPago SDK
        const mpInitialized = ensureMercadoPagoInitialized();

        if (!mpInitialized) {
          const initError = getMercadoPagoInitError();

          // Always log initialization errors to help identify root cause
          if (initError) {
            ErrorLogService.logError(initError, {
              component: "MercadoPagoPaymentBrick",
              severity: ErrorSeverity.WARNING,
              extra_data: {
                requestId,
                stage: "initialization_check",
                scriptLoaded: isMercadoPagoScriptLoaded(),
                isOnline:
                  typeof navigator !== "undefined" ? navigator.onLine : null,
              },
            }).catch(() => {});
          }

          // Don't set error state yet - let the user try anyway
          // The Payment component might still work if the script loads later
        }

        // Non-wallet flow: don't create a preference; just get the price unless overridden
        let finalAmount: number;
        if (typeof amountOverrideCents === "number") {
          if (canceled) return;
          finalAmount = amountOverrideCents;
        } else {
          const pricing = await pricingService.getPricingConfig();
          if (canceled) return;
          finalAmount = pricing.price_cents;
        }

        setAmount(finalAmount);
        logInitialization(finalAmount, mpInitialized);
      } catch (e) {
        if (!canceled) {
          const errorMsg =
            (e as Error)?.message || "Erro ao preparar pagamento.";
          setError(errorMsg);

          // Always log initialization errors to help identify root cause
          ErrorLogService.logError(
            e instanceof Error ? e : new Error(errorMsg),
            {
              component: "MercadoPagoPaymentBrick",
              severity: ErrorSeverity.ERROR,
              extra_data: {
                requestId,
                amountOverrideCents,
                stage: "initialization",
                errorStack: (e as Error)?.stack,
                isOnline:
                  typeof navigator !== "undefined" ? navigator.onLine : null,
              },
            },
          ).catch(() => {});
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, [requestId, amountOverrideCents]);

  useEffect(() => {
    return () => {
      if (pixCopyResetTimeoutRef.current !== null) {
        window.clearTimeout(pixCopyResetTimeoutRef.current);
      }
    };
  }, []);

  const showPixCopyFeedback = (
    message: string,
    tone: "success" | "error" | "info",
  ) => {
    setPixCopyFeedback({ id: Date.now(), message, tone });
    if (pixCopyResetTimeoutRef.current !== null) {
      window.clearTimeout(pixCopyResetTimeoutRef.current);
    }
    pixCopyResetTimeoutRef.current = window.setTimeout(() => {
      setPixCopyFeedback(null);
    }, 2500);
  };

  const showPixCopyAlert = (message: string) => {
    if (typeof window !== "undefined") {
      window.alert(message);
    }
  };

  const handlePixCopy = async (text: string) => {
    try {
      if (!navigator?.clipboard?.writeText) {
        const textArea = pixQrTextRef.current;
        if (textArea) {
          textArea.focus();
          textArea.select();
          textArea.setSelectionRange(0, textArea.value.length);
        }

        const copied =
          typeof document !== "undefined" &&
          document.queryCommandSupported?.("copy")
            ? document.execCommand("copy")
            : false;

        if (!copied) {
          const fallbackMessage = t(
            "payment.pix.copy_fallback",
            "Selecione e copie o codigo do Pix manualmente.",
          );
          showPixCopyFeedback(fallbackMessage, "info");
          showPixCopyAlert(fallbackMessage);
        } else {
          const successMessage = t(
            "payment.pix.copy_success",
            "Codigo Pix copiado!",
          );
          showPixCopyFeedback(successMessage, "success");
          showPixCopyAlert(successMessage);
        }

        return;
      }

      await navigator.clipboard.writeText(text);
      const successMessage = t(
        "payment.pix.copy_success",
        "Codigo Pix copiado!",
      );
      showPixCopyFeedback(successMessage, "success");
      showPixCopyAlert(successMessage);
    } catch (copyError) {
      const errorMessage = t(
        "payment.pix.copy_error",
        "Nao foi possivel copiar. Tente novamente.",
      );
      showPixCopyFeedback(errorMessage, "error");
      showPixCopyAlert(errorMessage);
      ErrorLogService.logError(
        copyError instanceof Error ? copyError : new Error("Pix copy failed"),
        {
          component: "MercadoPagoPaymentBrick",
          severity: ErrorSeverity.WARNING,
          extra_data: {
            requestId,
            stage: "pix_copy",
          },
        },
      ).catch(() => {});
    }
  };

  // Stable SDK props prevent its effect from recreating the Brick on error updates.
  const paymentBrick = useMemo(() => (
        <Payment
          id={paymentBrickId}
          key={String(amount)}
          initialization={{
            // Non-wallet flow: amount and payer email if available
            amount: amount === null ? 0 : Number((amount / 100).toFixed(2)),
            ...(payerEmail && isValidEmail(payerEmail)
              ? {
                  payer: {
                    email: payerEmail.trim(),
                  },
                }
              : {}),
          }}
          customization={{
            paymentMethods: {
              mercadoPago: [], // disable wallet for non-wallet flow
              bankTransfer: "all", // Pix
              creditCard: "all", // Cards
              debitCard: [],
              prepaidCard: [],
              ticket: [],
            },
            visual: {
              style: {
                theme: "default",
              },
            },
          }}
          onReady={() => {
            // hide external loaders if any
          }}
          onSubmit={({ selectedPaymentMethod, formData }) => {
              setError(null);
              // Step 1: Validate and extract data from brick (without transforming)
              let validatedData;
              try {
                validatedData = validateBrickSubmission(
                  selectedPaymentMethod,
                  formData,
                  requestId,
                );
              } catch (validationError) {
                const errorMsg = t(
                  "step8.error.invalid_brick_data",
                  "Invalid payment data received. Please try again.",
                );

                ErrorLogService.logError(
                  validationError instanceof Error
                    ? validationError
                    : new Error(String(validationError)),
                  {
                    component: "MercadoPagoPaymentBrick",
                    severity: ErrorSeverity.CRITICAL,
                    extra_data: {
                      requestId,
                      stage: "onSubmit_validation",
                      validationError: String(validationError),
                    },
                  },
                ).catch(() => {});

                setError(errorMsg);
                return Promise.reject(new Error(errorMsg));
              }

              const { spm, fdata, extractedEmail } = validatedData;

              // Step 2: Persist email to sessionStorage for analytics
              try {
                const extractedEmailSanitized = sanitizeEmail(extractedEmail);
                if (
                  extractedEmailSanitized &&
                  globalThis.window !== undefined
                ) {
                  sessionStorage.setItem(
                    "mp_payer_email",
                    extractedEmailSanitized,
                  );
                }
              } catch (storageError) {
                // Non-critical, just log
                console.warn(
                  "[MP] Failed to persist email to session:",
                  storageError,
                );
              }

              // Step 3: Determine final email (brick data takes priority over prop)
              const finalEmailRaw = extractedEmail || payerEmail;
              const finalEmail = sanitizeEmail(finalEmailRaw);
              const emailDiagnostics = getEmailDiagnostics(finalEmailRaw);

              // Step 4: Validate email before sending to backend
              if (!finalEmail || !isValidEmail(finalEmail)) {
                const errorMsg = t(
                  "step8.error.email_required",
                  "Please provide a valid email address.",
                );

                // Determine email source for logging
                let emailSource = "none";
                if (extractedEmail) {
                  emailSource = "brick";
                } else if (payerEmail) {
                  emailSource = "prop";
                }

                ErrorLogService.logError(
                  new Error(
                    "MercadoPago: Email validation failed at payment submit",
                  ),
                  {
                    component: "MercadoPagoPaymentBrick",
                    severity: ErrorSeverity.ERROR,
                    extra_data: {
                      requestId,
                      hasExtractedEmail: Boolean(extractedEmail),
                      hasPayerEmailProp: Boolean(payerEmail),
                      emailSource,
                      ...emailDiagnostics,
                      paymentMethodId:
                        spm.payment_method_id || spm.id || "unknown",
                    },
                  },
                ).catch(() => {});

                setError(errorMsg);
                return Promise.reject(new Error(errorMsg));
              }

              // Step 5: Prepare complete payload for backend
              const backendPayload = {
                selectedPaymentMethod: spm,
                formData: fdata,
                payer_email: finalEmail,
              };

              // Step 6: Log complete payload before sending (for debugging)
              ErrorLogService.logInfo("MP Backend Payload", {
                component: "MercadoPagoPaymentBrick",
                extra_data: {
                  requestId,
                  payload: safeStringify(backendPayload),
                  paymentMethodId: spm.payment_method_id || spm.id || "unknown",
                  paymentType: spm.type || spm.payment_type_id || "unknown",
                  hasToken: Boolean(fdata.token),
                  installments: fdata.installments || 1,
                },
              }).catch(() => {});

              // Step 7: Send to backend
              return TarotApiService.processMercadoPagoPaymentFromBrick(
                requestId,
                backendPayload,
              )
                .then((resp) => {
                  // Log successful response
                  ErrorLogService.logInfo("MP Payment Success", {
                    component: "MercadoPagoPaymentBrick",
                    extra_data: {
                      requestId,
                      paymentId: String(resp?.id || "unknown"),
                      status: String(resp?.status || "unknown"),
                      paymentMethodId: resp?.payment_method_id || "unknown",
                      hasPixArtifacts: Boolean(
                        resp?.pix_qr_base64 ||
                        resp?.pix_qr_text ||
                        resp?.pix_ticket_url,
                      ),
                      fullResponse: safeStringify(resp),
                    },
                  }).catch(() => {});

                  const resolvedPaymentId =
                    resp?.id !== undefined && resp?.id !== null
                      ? String(resp.id)
                      : undefined;
                  setLastPaymentId(resolvedPaymentId);

                  const status = String(resp?.status || "").toLowerCase();
                  const isApproved =
                    status === "approved" || status === "authorized";
                  const hasPixArtifacts = Boolean(
                    resp?.pix_qr_base64 ||
                    resp?.pix_qr_text ||
                    resp?.pix_ticket_url,
                  );

                  if (isApproved && !hasPixArtifacts) {
                    if (status === "approved" || status === "authorized") {
                      onSuccess?.(status);
                    }
                    return;
                  }

                  // Otherwise (Pix or waiting), show status and any Pix artifacts
                  if (resp?.pix_qr_base64) setPixQrBase64(resp.pix_qr_base64);
                  if (resp?.pix_qr_text) setPixQrText(resp.pix_qr_text);
                  if (resp?.pix_ticket_url)
                    setPixTicketUrl(resp.pix_ticket_url);
                  setShowStatus(true);
                })
                .catch((err) => {
                  // TarotApiService already logs API errors with endpoint/status
                  // We only log additional context here (payload sent, component state)

                  let errorMessage: string;
                  if (err instanceof Error) {
                    errorMessage = err.message;
                  } else if (typeof err === "string") {
                    errorMessage = err;
                  } else {
                    errorMessage = "Erro desconhecido";
                  }

                  // Check if it's an email validation error
                  const isEmailError =
                    errorMessage.includes(
                      "payer.email must be a valid email",
                    ) ||
                    errorMessage.includes("payer_email") ||
                    errorMessage.includes("email");

                  // Only log additional context that wasn't captured by TarotApiService
                  // (payload details, payment method, component state)
                  ErrorLogService.logInfo("MP Payment Context", {
                    component: "MercadoPagoPaymentBrick",
                    extra_data: {
                      requestId,
                      stage: "backend_processing",
                      paymentMethodId:
                        spm.payment_method_id || spm.id || "unknown",
                      amount,
                      isEmailError,
                      ...(isEmailError
                        ? {
                            emailDiagnostics,
                            payerEmailRaw: finalEmailRaw || null,
                            payerEmailSanitized: finalEmail || null,
                          }
                        : {}),
                      // Log the FULL payload that was sent (this is unique info)
                      sentPayload: safeStringify(backendPayload),
                      // Reference to the original error message
                      apiErrorMessage: errorMessage,
                    },
                  }).catch(() => {});

                  // User-friendly error message
                  let displayMessage = errorMessage;
                  if (isEmailError) {
                    displayMessage = getEmailErrorMessage(errorMessage, "step8.error.email_invalid");
                  }

                  setError(displayMessage);
                  setShowStatus(false);
                  throw err;
                });
          }}
          onError={(err: unknown) => {
              let errorMessage: string;
              if (err instanceof Error) {
                errorMessage = err.message;
              } else if (typeof err === "string") {
                errorMessage = err;
              } else if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
                errorMessage = err.message;
              } else {
                errorMessage = "Erro desconhecido";
              }

              const errObj =
                err && typeof err === "object"
                  ? (err as Record<string, unknown>)
                  : null;
              const mpCause =
                errObj && typeof errObj.cause === "string"
                  ? errObj.cause
                  : undefined;
              const mpType =
                errObj && typeof errObj.type === "string"
                  ? errObj.type
                  : undefined;
              const mpMessage =
                errObj && typeof errObj.message === "string"
                  ? errObj.message
                  : undefined;
              const isCardTokenError =
                mpCause === "secure_fields_card_token_creation_failed";

              // Check if it's an email validation error
              const isEmailError =
                errorMessage.includes("payer.email must be a valid email") ||
                errorMessage.includes("payer_email") ||
                errorMessage.includes("email");

              // Log brick-specific errors (these are different from API errors)
              ErrorLogService.logError(new Error("[MP Brick Error]"), {
                component: "MercadoPagoPaymentBrick",
                severity: ErrorSeverity.ERROR,
                extra_data: {
                  requestId,
                  stage: "brick_onError",
                  amount,
                  isEmailError,
                  mpCause,
                  mpType,
                  mpMessage,
                  errorMessage,
                  errorType: typeof err,
                  errorStack: err instanceof Error ? err.stack : undefined,
                  errorObject: safeStringify(err),
                  hasPayerEmail: Boolean(payerEmail),
                  payerEmailValid: payerEmail ? isValidEmail(payerEmail) : null,
                },
              }).catch(() => {});

              // User-friendly error message
              let displayMessage = errorMessage;
              if (isEmailError) {
                displayMessage = getEmailErrorMessage(errorMessage, "step8.error.email_required");
              }
              if (isCardTokenError && !isEmailError) {
                displayMessage = t(
                  "step8.error.card_token_failed",
                  "Não foi possível validar o cartão. Verifique os dados e tente novamente.",
                );
              }
              // Causas mais comuns nos logs; sem o mapeamento o usuário via
              // "Erro desconhecido" e tentava o mesmo cartão repetidas vezes.
              if (mpMessage === "no_payment_method_for_provided_bin") {
                displayMessage = t(
                  "step8.error.bin_not_recognized",
                  "Não reconhecemos o número deste cartão. Confira os dígitos ou tente outro cartão (ou Pix).",
                );
              }
              if (mpMessage === "payment_method_not_in_allowed_types") {
                displayMessage = t(
                  "step8.error.method_not_allowed",
                  "Este tipo de cartão não é aceito para esta compra. Tente um cartão de crédito ou Pix.",
                );
              }

              setError(displayMessage);
              setShowStatus(false);
          }}
        />
  ), [amount, payerEmail, requestId, paymentBrickId, t, onSuccess, getEmailErrorMessage]);

  // Success screen is rendered by the parent component

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8 rounded-lg"
        style={{
          background: "linear-gradient(135deg, #fef9f0 0%, #ffffff 100%)",
          border: "1px solid #d4af37",
          minHeight: "200px",
        }}
      >
        <div className="relative w-16 h-16 mb-4">
          <div
            className="absolute inset-0 rounded-full animate-spin"
            style={{
              background: "linear-gradient(135deg, #d4af37, #f4e4b6, #d4af37)",
              animation: "spin 1.5s linear infinite",
            }}
          />
          <div className="absolute inset-2 rounded-full bg-white" />
          <div className="absolute inset-0 flex items-center justify-center text-2xl">
            💳
          </div>
        </div>
        <p className="text-base font-title text-tarot-marrom-escuro animate-pulse">
          {t("payment.loading", "Carregando pagamento…")}
        </p>
      </div>
    );
  }

  if (error && amount == null)
    return (
      <div className="text-red-600 p-4 rounded-lg bg-red-50 border border-red-200">
        {error}
      </div>
    );
  if (amount == null)
    return (
      <div className="text-orange-600 p-4 rounded-lg bg-orange-50 border border-orange-200">
        {t("payment.error", "Não foi possível obter o pagamento.")}
      </div>
    );

  return (
    <div>
      {error && (
        <div role="alert" className="mb-4 text-red-600 p-4 rounded-lg bg-red-50 border border-red-200">
          {error}
        </div>
      )}
      {/* Keep Payment mounted even when showing status to avoid MP async create/unmount race. */}
      <div
        className={`-mx-4 sm:mx-0 ${showStatus ? "hidden" : ""}`}
        aria-hidden={showStatus}
      >
        {paymentBrick}
      </div>

      {showStatus && (
        <div className="mt-4 space-y-4">
          {/* Pix QR section (if available) */}
          {(pixQrBase64 || pixQrText || pixTicketUrl) && (
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="text-base font-semibold mb-3">Pague com Pix</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="flex flex-col items-center justify-center">
                  {pixQrBase64 ? (
                    <Image
                      src={`data:image/png;base64,${pixQrBase64}`}
                      alt="QR Code Pix"
                      width={192}
                      height={192}
                      unoptimized
                      className="w-48 h-48 object-contain border rounded"
                    />
                  ) : (
                    <div className="w-48 h-48 border rounded flex items-center justify-center text-sm text-gray-500">
                      QR indisponível
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {pixQrText && (
                    <div>
                      <label
                        htmlFor="pix-code"
                        className="text-sm text-gray-600"
                      >
                        Copia e cola do Pix
                      </label>
                      <textarea
                        id="pix-code"
                        className="mt-1 w-full text-xs font-mono border rounded p-2 h-24"
                        readOnly
                        value={pixQrText}
                        ref={pixQrTextRef}
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="px-3 py-1 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={() => handlePixCopy(pixQrText)}
                        >
                          {pixCopyFeedback?.tone === "success"
                            ? "Copiado!"
                            : "Copiar codigo"}
                        </button>
                        {pixTicketUrl && (
                          <a
                            href={pixTicketUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Abrir no app do banco
                          </a>
                        )}
                      </div>
                      {lastPaymentId && (
                        <p className="mt-2 text-xs text-gray-500">
                          Pagamento MP: {String(lastPaymentId)}
                        </p>
                      )}
                      {pixCopyFeedback && (
                        <p
                          key={pixCopyFeedback.id}
                          className={
                            pixCopyFeedback.tone === "error"
                              ? "mt-2 text-xs text-red-600"
                              : pixCopyFeedback.tone === "info"
                                ? "mt-2 text-xs text-blue-700"
                                : "mt-2 text-xs text-emerald-700"
                          }
                          role="status"
                          aria-live="polite"
                        >
                          {pixCopyFeedback.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {lastPaymentId ? (
            <MercadoPagoStatusScreen
              requestId={requestId}
              paymentId={lastPaymentId}
              onSuccess={(s) => onSuccess?.(s)}
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600">
              {t(
                "payment.status.pending",
                "Pagamento em processamento. Aguarde a confirmação.",
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
